<template>
  <div
      class="character-card"
      :style="bgStyle"
      :class="{ 'no-action': noActionPoints && !deathOverlay }"
      @click="$emit('click')"
  >

    <!-- Horizontal Name of the Character -->
    <div class="character-name">{{ name }}</div>
    <!-- Vitals plinth at the foot of the portrait -->
    <div class="card-vitals">
      <div class="card-vitals__readout">
        <span v-for="attribute in attributeBars" :key="attribute.name" class="card-vitals__figure">
          <i>{{ tagFor(attribute.name) }}</i>
          {{ attribute.hasData ? `${attribute.current}/${attribute.max}` : '—' }}
        </span>
      </div>
      <AttributeBar
          v-for="attribute in attributeBars"
          :key="attribute.name"
          :current="attribute.current"
          :max="attribute.max"
          :type="attribute.name"
      />
    </div>
    <!-- Death or knockout overlay -->
    <div v-if="deathOverlay" class="death-overlay"></div>
    <!-- Red overlay for low health -->
    <div v-else-if="lowHealth" class="low-health-overlay"></div>

  </div>
</template>

<script>

import AttributeBar from "@/components/GameMaster/Character/AttributeBar.vue";

const VITALS = ["Health", "Energy", "Action Points"];

export default {
  name: "CharacterCard",
  components: {AttributeBar},
  emits: ["click"],
  props: {
    icon: {type: String, required: true},
    name: {type: String, required: true},
    // details is expected to include an "attributes" array.
    details: {type: Object, default: () => ({})}
  },
  methods: {
    tagFor(name) {
      return { Health: "HP", Energy: "EN", "Action Points": "AP" }[name] || name;
    },
  },
  computed: {
    bgStyle() {
      return {
        backgroundImage: `url(${this.icon})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      };
    },
    // Always the same three rows in the same order, so a row of cards reads as
    // one instrument even when a character reports no attributes yet.
    attributeBars() {
      const attrs = this.details?.attributes || [];
      return VITALS.map(name => {
        const attr = attrs.find(a => a.name === name);
        return {
          name,
          current: attr?.current ?? 0,
          max: attr?.max ?? 0,
          hasData: Number.isFinite(attr?.max) && attr.max > 0,
        };
      });
    },
    // Determines if the character has no action points
    noActionPoints() {
      const attrArr = this.details?.attributes || [];
      const actionAttr = attrArr.find(attr =>
          attr.name.toLowerCase().includes("action")
      );
      return actionAttr ? actionAttr.current === 0 : false;
    },
    // Determines if the character's health is below 10
    lowHealth() {
      const attrArr = this.details?.attributes || [];
      const healthAttr = attrArr.find(attr => attr.name.toLowerCase() === "health");
      return healthAttr ? healthAttr.current < 10 : false;
    },
    // Death or knockout overlay
    deathOverlay() {
      const attrArr = this.details?.attributes || [];
      const healthAttr = attrArr.find(attr => attr.name.toLowerCase() === "health");
      return healthAttr ? healthAttr.current <= 0 : false;
    }
  }
};
</script>

<style scoped>
.character-card {
  flex: none;
  height: 5.5rem;
  width: 3.75rem;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  overflow: hidden;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.character-card:hover {
  border-color: var(--gm-border);
  transform: translateY(-1px);
}

.character-card.selected {
  border-color: var(--gm-accent);
  box-shadow: 0 0 0 1px var(--gm-accent), 0 0 12px rgba(250, 218, 149, 0.35);
}

/* Character name at the top */
.character-name {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0));
  font-family: var(--gm-font-display);
  color: var(--gm-text);
  font-size: 0.45rem;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.25rem 0.5rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.character-card.selected .character-name {
  color: var(--gm-accent);
}


/* Vitals plinth */
.card-vitals {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 0.16rem;
  padding: 1rem 0.25rem 0.25rem;
  background: linear-gradient(to top, rgba(7, 9, 10, 0.94) 42%, rgba(7, 9, 10, 0));
  pointer-events: none;
}

/* Numbers are unreadable at card scale, so they wait for a pointer. */
.card-vitals__readout {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.25rem;
  background: rgba(7, 9, 10, 0.92);
  font-family: var(--gm-font-display);
  font-size: 0.42rem;
  line-height: 1;
  letter-spacing: 0.02em;
  color: var(--gm-text-muted);
  font-variant-numeric: tabular-nums;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.card-vitals__figure {
  display: flex;
  justify-content: space-between;
  gap: 0.3rem;
}

.card-vitals__figure i {
  font-style: normal;
  color: var(--gm-text-dim);
}

.character-card:hover .card-vitals__readout {
  opacity: 1;
}

.card-vitals__figure:first-child {
  color: var(--gm-text);
}

/* Low health: an ember rim, not a wash over the portrait */
.low-health-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow:
      inset 0 0 0 1px rgba(201, 69, 63, 0.75),
      inset 0 -1.2rem 1.4rem -0.9rem rgba(201, 69, 63, 0.6);
}

.death-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("@/assets/images/avatar/death.webp" ) no-repeat center;
  background-size: contain;
  opacity: 0.72;
  pointer-events: none;
}



/* Gray out the card if no action points */
.no-action {
  filter: grayscale(100%);
  opacity: 0.7;
}

</style>
